import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { 
  ClipboardCheck, 
  Search, 
  Camera, 
  MapPin, 
  Clock, 
  User,
  Car,
  AlertTriangle,
  Fuel,
  Gauge,
  ChevronLeft,
  ChevronRight,
  Loader2,
  CheckCircle,
  Image as ImageIcon,
  FileText
} from 'lucide-react';
import ManualDamageReporter from '@/components/inspections/ManualDamageReporter';

function getClientBackendBaseUrl() {
  const env = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/+$/, '');
  if (env) return env;
  if (typeof window !== 'undefined' && window.location?.origin) {
    return String(window.location.origin).replace(/\/+$/, '');
  }
  return '';
}

function formatPhotoAiSnippet(ai) {
  if (ai == null) return '';
  if (typeof ai === 'string') return ai.trim();
  if (typeof ai === 'object') {
    const summary = ai.summary || ai.description || ai.text;
    if (typeof summary === 'string' && summary.trim()) return summary.trim();
    try {
      return JSON.stringify(ai);
    } catch {
      return String(ai);
    }
  }
  return String(ai);
}

const ClientInspectionsTab = () => {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language?.startsWith('es') ? 'es-ES' : 'en-US';
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedInspection, setSelectedInspection] = useState(null);
  const [pickupInspection, setPickupInspection] = useState(null);
  const [deliveryInspection, setDeliveryInspection] = useState(null);
  const [activeInspectionKind, setActiveInspectionKind] = useState('pickup');
  const [modalOpen, setModalOpen] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [currentUserId, setCurrentUserId] = useState(null);

  // Fetch the logged-in client's id once so they can delete their own manual
  // damage reports. The endpoint is gentle on failure — without an id, the
  // delete button just stays hidden, which is the safest default.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const base = getClientBackendBaseUrl();
      if (!base) return;
      try {
        const token = localStorage.getItem('session_token');
        if (!token) return;
        const res = await fetch(`${base}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setCurrentUserId(data?.id || data?.user?.id || null);
      } catch (_) {
        /* swallow — falls back to no-delete-permission UI */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const damageCount = (insp) => {
    if (!insp) return 0;
    const arr = insp.ai_summary?.damages_detected;
    return Array.isArray(arr) ? arr.length : 0;
  };
  const serviceHasDamage = (s) =>
    damageCount(s.pickup_inspection) > 0 || damageCount(s.delivery_inspection) > 0;

  const fetchServices = useCallback(async () => {
    const base = getClientBackendBaseUrl();
    if (!base) {
      setServices([]);
      setLoading(false);
      return;
    }
    try {
      const token = localStorage.getItem('session_token');
      const response = await fetch(`${base}/api/client/transport-services`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Filter services that have inspections
        const servicesWithInspections = (data.services || []).filter(
          s => s.has_pickup_inspection || s.has_delivery_inspection
        );
        setServices(servicesWithInspections);
      }
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  const fetchServiceDetail = async (serviceId) => {
    const base = getClientBackendBaseUrl();
    if (!base) return;
    try {
      const token = localStorage.getItem('session_token');
      const response = await fetch(`${base}/api/client/transport-services/${serviceId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        const pickup = data.pickup_inspection
          ? { ...data.pickup_inspection, inspection_type: data.pickup_inspection.inspection_type || 'pickup', service_info: data }
          : null;
        const delivery = data.delivery_inspection
          ? { ...data.delivery_inspection, inspection_type: data.delivery_inspection.inspection_type || 'delivery', service_info: data }
          : null;
        if (!pickup && !delivery) return;
        const initial = pickup || delivery;
        setPickupInspection(pickup);
        setDeliveryInspection(delivery);
        setActiveInspectionKind(pickup ? 'pickup' : 'delivery');
        setSelectedInspection(initial);
        setCurrentPhotoIndex(0);
        setModalOpen(true);
      }
    } catch (error) {
      console.error('Error fetching service detail:', error);
    }
  };

  const switchInspectionKind = (kind) => {
    if (kind === activeInspectionKind) return;
    const next = kind === 'pickup' ? pickupInspection : deliveryInspection;
    if (!next) return;
    setActiveInspectionKind(kind);
    setSelectedInspection(next);
    setCurrentPhotoIndex(0);
  };

  const filteredServices = services.filter(service => {
    const searchLower = searchTerm.toLowerCase();
    return (
      service.vehicle_info?.plate?.toLowerCase().includes(searchLower) ||
      service.vehicle_info?.brand?.toLowerCase().includes(searchLower)
    );
  });

  const formatDate = (isoString) => {
    if (!isoString) return t('client_na');
    const date = new Date(isoString);
    return date.toLocaleDateString(dateLocale, { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getInspectionTypeBadge = (type) => {
    if (type === 'pickup') {
      return <Badge className="bg-blue-100 text-blue-700">{t('client_inspections_type_pickup')}</Badge>;
    }
    return <Badge className="bg-green-100 text-green-700">{t('client_inspections_type_delivery')}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-green-600" />
        <span className="ml-2 text-gray-600">{t('client_loading_inspections')}</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="client-inspections-tab">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ClipboardCheck className="w-6 h-6 text-purple-600" />
            {t('client_inspections_title')}
          </h2>
          <p className="text-gray-600 mt-1">
            {t('client_inspections_subtitle')}
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          placeholder={t('client_inspections_search_placeholder')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <FileText className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{services.length}</p>
                <p className="text-sm text-gray-500">{t('client_inspections_stat_total')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {services.filter(s => !serviceHasDamage(s)).length}
                </p>
                <p className="text-sm text-gray-500">{t('client_inspections_stat_no_damage')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {services.filter(serviceHasDamage).length}
                </p>
                <p className="text-sm text-gray-500">{t('client_inspections_stat_with_damage')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Services with Inspections List */}
      {filteredServices.length > 0 ? (
        <div className="grid gap-4">
          {filteredServices.map((service) => (
            <Card 
              key={service.id} 
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => fetchServiceDetail(service.id)}
            >
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  {/* Vehicle Info */}
                  <div className="flex items-center gap-4">
                    <div className="relative">
                      <div className="w-16 h-16 bg-purple-100 rounded-lg flex items-center justify-center">
                        <Camera className="w-8 h-8 text-purple-500" />
                      </div>
                    </div>
                    
                    <div>
                      <div className="flex items-center gap-2">
                        <Car className="w-4 h-4 text-gray-500" />
                        <span className="font-bold text-lg">{service.vehicle_info?.plate}</span>
                      </div>
                      <p className="text-gray-600 text-sm">
                        {service.vehicle_info?.brand} {service.vehicle_info?.model}
                      </p>
                      <div className="flex gap-2 mt-1">
                        {service.has_pickup_inspection && (
                          <Badge variant="outline" className="text-xs">{t('client_inspections_badge_pickup')}</Badge>
                        )}
                        {service.has_delivery_inspection && (
                          <Badge variant="outline" className="text-xs">{t('client_inspections_badge_delivery')}</Badge>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Route Summary */}
                  <div className="flex-1 max-w-md text-sm">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-600 truncate">
                        {service.pickup_location?.address} → {service.delivery_location?.address}
                      </span>
                    </div>
                  </div>

                  {/* Status & Date */}
                  <div className="text-sm text-right">
                    <Badge className={service.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}>
                      {service.status === 'completed' ? t('client_inspections_status_delivered') : t('client_inspections_status_in_progress')}
                    </Badge>
                    <div className="flex items-center gap-1 justify-end mt-1 text-gray-500">
                      <Clock className="w-3 h-3" />
                      {formatDate(service.completed_at || service.created_at)}
                    </div>
                  </div>

                  <Button variant="outline" size="sm">
                    {t('client_inspections_view_btn')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <ClipboardCheck className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900">{t('client_inspections_empty_title')}</h3>
              <p className="text-gray-500 mt-1">
                {searchTerm 
                  ? t('client_inspections_empty_search')
                  : t('client_inspections_empty_hint')}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Inspection Detail Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          {selectedInspection && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 flex-wrap">
                  <Car className="w-5 h-5" />
                  {t('client_inspections_modal_title')} — {selectedInspection.vehicle_plate || selectedInspection.service_info?.vehicle_info?.plate}
                  {getInspectionTypeBadge(activeInspectionKind)}
                </DialogTitle>
              </DialogHeader>

              {(pickupInspection && deliveryInspection) && (
                <div className="flex gap-2 mb-2">
                  <Button
                    type="button"
                    variant={activeInspectionKind === 'pickup' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => switchInspectionKind('pickup')}
                    disabled={!pickupInspection}
                  >
                    {t('client_inspections_type_pickup')}
                    {damageCount(pickupInspection) > 0 && (
                      <Badge className="ml-2 bg-orange-100 text-orange-700">
                        {damageCount(pickupInspection)}
                      </Badge>
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant={activeInspectionKind === 'delivery' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => switchInspectionKind('delivery')}
                    disabled={!deliveryInspection}
                  >
                    {t('client_inspections_type_delivery')}
                    {damageCount(deliveryInspection) > 0 && (
                      <Badge className="ml-2 bg-orange-100 text-orange-700">
                        {damageCount(deliveryInspection)}
                      </Badge>
                    )}
                  </Button>
                </div>
              )}

              <div className="space-y-6">
                {/* Photo Gallery */}
                {selectedInspection.photos?.length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-3 flex items-center gap-2">
                      <Camera className="w-4 h-4" />
                      {t('client_inspections_vehicle_photos', { count: selectedInspection.photos.length })}
                    </h4>
                    
                    {/* Main Photo */}
                    <div className="relative bg-gray-100 rounded-lg overflow-hidden">
                      <img 
                        src={selectedInspection.photos[currentPhotoIndex]?.url}
                        alt={t('client_inspections_photo_alt', { n: currentPhotoIndex + 1 })}
                        className="w-full h-80 object-contain"
                      />
                      
                      {/* Navigation Arrows */}
                      {selectedInspection.photos.length > 1 && (
                        <>
                          <button
                            type="button"
                            onClick={() => setCurrentPhotoIndex(i => i === 0 ? selectedInspection.photos.length - 1 : i - 1)}
                            className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-black/50 rounded-full text-white hover:bg-black/70"
                            aria-label={t('client_inspections_photo_prev_aria')}
                          >
                            <ChevronLeft className="w-5 h-5" aria-hidden />
                          </button>
                          <button
                            type="button"
                            onClick={() => setCurrentPhotoIndex(i => i === selectedInspection.photos.length - 1 ? 0 : i + 1)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-black/50 rounded-full text-white hover:bg-black/70"
                            aria-label={t('client_inspections_photo_next_aria')}
                          >
                            <ChevronRight className="w-5 h-5" aria-hidden />
                          </button>
                        </>
                      )}
                      
                      {/* Photo Type Badge */}
                      <div className="absolute bottom-2 left-2">
                        <Badge className="bg-black/70 text-white">
                          {selectedInspection.photos[currentPhotoIndex]?.type || t('client_inspections_photo_fallback')}
                        </Badge>
                      </div>
                      
                      {/* Counter */}
                      <div className="absolute bottom-2 right-2 bg-black/70 text-white px-2 py-1 rounded text-sm">
                        {currentPhotoIndex + 1} / {selectedInspection.photos.length}
                      </div>
                    </div>

                    {/* Thumbnails */}
                    <div className="flex gap-2 mt-3 overflow-x-auto pb-2">
                      {selectedInspection.photos.map((photo, index) => (
                        <button
                          key={index}
                          onClick={() => setCurrentPhotoIndex(index)}
                          className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 ${
                            index === currentPhotoIndex ? 'border-green-500' : 'border-transparent'
                          }`}
                        >
                          <img 
                            src={photo.url} 
                            alt={t('client_inspections_thumbnail_alt', { n: index + 1 })}
                            className="w-full h-full object-cover"
                          />
                        </button>
                      ))}
                    </div>

                    {/* AI Analysis for current photo */}
                    {selectedInspection.photos[currentPhotoIndex]?.ai_analysis != null &&
                      String(formatPhotoAiSnippet(selectedInspection.photos[currentPhotoIndex].ai_analysis)).trim() !== '' && (
                      <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                        <p className="text-sm text-blue-800 whitespace-pre-wrap break-words">
                          <strong>{t('client_inspections_ai_label')}</strong>{' '}
                          {formatPhotoAiSnippet(selectedInspection.photos[currentPhotoIndex].ai_analysis)}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* AI Summary */}
                {selectedInspection.ai_summary && (
                  <div>
                    <h4 className="font-semibold mb-3 flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      {t('client_inspections_ai_summary_title')}
                    </h4>
                    
                    <div className="grid md:grid-cols-2 gap-4">
                      {/* Damages */}
                      <Card>
                        <CardContent className="p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <AlertTriangle className={`w-4 h-4 ${selectedInspection.ai_summary.damages_detected?.length ? 'text-red-500' : 'text-green-500'}`} />
                            <span className="font-medium">{t('client_inspections_vehicle_state')}</span>
                          </div>
                          {selectedInspection.ai_summary.damages_detected?.length > 0 ? (
                            <div>
                              <p className="text-sm text-red-600 mb-2">{t('client_inspections_damages_detected')}</p>
                              <ul className="list-disc list-inside text-sm text-gray-600">
                                {selectedInspection.ai_summary.damages_detected.map((damage, i) => (
                                  <li key={i}>{damage}</li>
                                ))}
                              </ul>
                            </div>
                          ) : (
                            <p className="text-sm text-green-600">{t('client_inspections_no_damage_ai')}</p>
                          )}
                        </CardContent>
                      </Card>

                      {/* Vehicle Stats */}
                      <Card>
                        <CardContent className="p-4">
                          <div className="space-y-3">
                            <div className="flex items-center justify-between">
                              <span className="flex items-center gap-2 text-gray-600">
                                <Gauge className="w-4 h-4" />
                                {t('client_inspections_odometer')}
                              </span>
                              <span className="font-medium">{selectedInspection.ai_summary.odometer_reading || t('client_na')}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="flex items-center gap-2 text-gray-600">
                                <Fuel className="w-4 h-4" />
                                {t('client_inspections_fuel')}
                              </span>
                              <span className="font-medium">{selectedInspection.ai_summary.fuel_level || t('client_na')}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-gray-600">{t('client_inspections_general_condition')}</span>
                              <Badge variant="outline">{selectedInspection.ai_summary.overall_condition || t('client_na')}</Badge>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                )}

                {/* Manual damage reporting — clients can flag what the AI missed.
                    Backend enforces ownership; we only show the delete button on
                    entries this same user authored (currentUserId is fetched once
                    on mount via /api/auth/me). */}
                <ManualDamageReporter
                  inspection={selectedInspection}
                  role="client"
                  currentUserId={currentUserId}
                  onUpdated={(updated) => {
                    setSelectedInspection(updated);
                    if (activeInspectionKind === 'pickup') setPickupInspection(updated);
                    else if (activeInspectionKind === 'delivery') setDeliveryInspection(updated);
                  }}
                />

                {/* Signature */}
                {selectedInspection.signature && (
                  <div>
                    <h4 className="font-semibold mb-3">{t('client_inspections_signature_title')}</h4>
                    <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                      {selectedInspection.signature.data_url && (
                        <img 
                          src={selectedInspection.signature.data_url} 
                          alt={t('client_inspections_signature_alt')}
                          className="h-16 border rounded bg-white"
                        />
                      )}
                      {selectedInspection.signature.signer_name && (
                        <span className="text-gray-600">{selectedInspection.signature.signer_name}</span>
                      )}
                    </div>
                  </div>
                )}

                {/* Location & Time */}
                <div className="grid md:grid-cols-2 gap-4 text-sm p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2 text-gray-600">
                    <MapPin className="w-4 h-4" />
                    <span>{selectedInspection.location?.address || t('inspections_location_unavailable')}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600">
                    <Clock className="w-4 h-4" />
                    <span>{formatDate(selectedInspection.created_at)}</span>
                  </div>
                </div>

                {/* Driver Info */}
                {selectedInspection.driver_info && (
                  <div className="flex items-center gap-2 text-gray-600 text-sm">
                    <User className="w-4 h-4" />
                    <span>{t('client_inspections_done_by')} <strong>{selectedInspection.driver_info.name}</strong></span>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientInspectionsTab;
